/* Central list of API endpoints (helpers for dynamic params) */
export const API_ENDPOINTS = {
  // Customer service
  CUSTOMER: {
    SESSIONS: '/customer/sessions',
    SESSIONS_BY_ID: (sessionId: string) => `/customer/sessions/${sessionId}`,
    CART: (sessionId: string) => `/customer/sessions/${sessionId}/cart`,
    CART_ITEM: (sessionId: string, itemId: number | string) =>
      `/customer/sessions/${sessionId}/cart/${itemId}`,
    MENU: (sessionId: string) => `/customer/sessions/${sessionId}/menu`,
    SESSION_ORDERS: (sessionId: string) => `/customer/sessions/${sessionId}/orders`,
    SESSION_PAYMENTS: (sessionId: string) => `/customer/sessions/${sessionId}/payments`,
    // Where the customer lands back after the provider's hosted checkout page.
    SESSION_PAYMENT_COMPLETE: (sessionId: string) => `/customer/sessions/${sessionId}/payments/complete`,
    SESSION_FEEDBACK: (sessionId: string) => `/customer/sessions/${sessionId}/feedback`,
    QR_SCAN: '/customer/qr/scan',
    MY_DASHBOARD: '/customer/my/dashboard',
    MY_HISTORY_VISITS: '/customer/my/history/visits',
    MY_HISTORY_EXPENDITURE: '/customer/my/history/expenditure',
    TABLES_AVAILABLE: '/customer/tables/available',
    CUSTOMERS: '/customer/customers',
    FEEDBACK: '/customer/feedback'
  },

  // Inventory / Drinks service
  DRINKS: {
    LIST: '/drinks',
    DETAIL: (id: number | string) => `/drinks/${id}`,
    STOCK: (id: number | string) => `/drinks/${id}/stock`,
    REDUCE_STOCK: (id: number | string) => `/drinks/${id}/reduce-stock`,
    BULK_STOCK: '/drinks/bulk-stock',
    LOW_STOCK: '/drinks/low-stock',
    AVAILABLE: '/drinks/available',
    AVAILABILITY: (id: number | string) => `/drinks/${id}/available`,
    BY_NAME: (name: string) => `/drinks/name/${encodeURIComponent(name)}`,
    BY_CATEGORY: (categoryId: number | string) => `/drinks/category/${categoryId}`,
    BY_CATEGORY_PAGE: (categoryId: number | string) => `/drinks/category/${categoryId}/page`,
    SEARCH: '/drinks/search',
    INVENTORY_VALUE: '/drinks/inventory-value',
    PRICE_RANGE: '/drinks/price-range',
    TOP_RATED: (categoryId: number | string) => `/drinks/top-rated/${categoryId}`,
  },

  // Orders service
  ORDERS: {
    CREATE: '/orders',
    LIST: '/orders',
    DETAIL: (id: number | string) => `/orders/${id}`,
    STATUS: (id: number | string) => `/orders/${id}/status`,
    BY_STATUS: (status: string) => `/orders/status/${status}`,
    READY_FOR_PICKUP: '/orders/ready-for-pickup',
    OVERDUE: '/orders/overdue',
    BY_TABLE: (tableNumber: string) => `/orders/table/${tableNumber}`,
    BY_CUSTOMER: (customerName: string) => `/orders/customer/${encodeURIComponent(customerName)}`,
    BY_INVOICE: (invoiceNumber: string) => `/orders/invoice/${encodeURIComponent(invoiceNumber)}`,
    CANCEL: (id: number | string) => `/orders/${id}/cancel`,
    STATISTICS: (startDate: string, endDate: string) => `/orders/statistics?startDate=${startDate}&endDate=${endDate}`,
    SEARCH: '/orders/search',
  },

  // Payments service
  PAYMENTS: {
    INTENT: '/payments/intent',
    CALLBACK: '/payments/callback',
    LIST: '/payments',
    PROCESS: '/payments',
    DETAILS: (id: number | string) => `/payments/${id}`,
    TRANSACTION: (txId: string) => `/payments/transaction/${encodeURIComponent(txId)}`,
    ORDER_PAYMENTS: (orderId: number | string) => `/payments/order/${orderId}`,
    BY_INVOICE: (invoiceNumber: string) => `/payments/invoice/${encodeURIComponent(invoiceNumber)}`,
    BY_STATUS: (status: string) => `/payments/status/${status}`,
    BY_METHOD: (method: string) => `/payments/method/${method}`,
    DATE_RANGE: '/payments/date-range',
    FAILED: '/payments/failed',
    REFUNDED: '/payments/refunded',
    SEARCH: '/payments/search',
    TOP_METHODS_COUNT: '/payments/top-methods/count',
    TOP_METHODS_AMOUNT: '/payments/top-methods/amount',
    REFUND: (id: number | string) => `/payments/${id}/refund`,
    // A cash payment stays PENDING until someone at the bar confirms the
    // money is in hand.
    CONFIRM_CASH: (id: number | string) => `/payments/${id}/confirm-cash`,
    STATUS: (id: number | string, status: string) => `/payments/${id}/status?status=${status}`,
    STATISTICS: (startDate: string, endDate: string) => `/payments/statistics?startDate=${startDate}&endDate=${endDate}`,
  },

  // QR service
  QR: {
    GENERATE: '/qr-codes',
    CODES: '/qr-codes',
    STATISTICS: '/qr-codes/statistics',
    CODE_DETAIL: (id: number | string) => `/qr-codes/${id}`,
    SCAN: '/qr-codes/scan',
    SCAN_INFO: (qrCode: string) => `/qr-codes/data/${encodeURIComponent(qrCode)}`,
    VALIDATE: (qrCode: string) => `/qr-codes/validate/${encodeURIComponent(qrCode)}`,
    TABLE_INFO: (qrCode: string) => `/qr-codes/${encodeURIComponent(qrCode)}/table`,
    TABLES: '/qr-codes/table',
    TABLE_DETAIL: (tableNumber: string) => `/qr-codes/table/${tableNumber}`,
    TABLE_QR: (tableNumber: string) => `/qr-codes/table/${tableNumber}`,
    MENU_QR: '/qr-codes/menu',
    ACTIVE: '/qr-codes/active',
    SCANNABLE: '/qr-codes/scannable',
    BY_TYPE: (type: string) => `/qr-codes/type/${type}`,
    MOST_SCANNED: '/qr-codes/most-scanned',
    RECENTLY_SCANNED: '/qr-codes/recently-scanned',
    SEARCH: '/qr-codes/search',
    AVAILABLE_TABLES: '/qr-codes/tables/available',
  },

  // Staff service
  STAFF: {
    LIST: '/staff',
    CREATE: '/staff',
    DETAIL: (id: number | string) => `/staff/${id}`,
    UPDATE: (id: number | string) => `/staff/${id}`,
    DELETE: (id: number | string) => `/staff/${id}`,
    PERFORMANCE: (id: number | string) => `/staff/${id}/performance`,
    CLOCK_IN: (id: number | string) => `/staff/${id}/clock-in`,
    CLOCK_OUT: (id: number | string) => `/staff/${id}/clock-out`,
    TIPS: (id: number | string) => `/staff/${id}/tips`,
    STATUS: (id: number | string) => `/staff/${id}/status`,
    BY_EMAIL: (email: string) => `/staff/email/${encodeURIComponent(email)}`,
    BY_ROLE: (role: string) => `/staff/role/${role}`,
    ACTIVE: '/staff/active',
    SEARCH: '/staff/search',
    RECENTLY_ACTIVE: '/staff/recently-active',
    TOP_PERFORMERS_TIPS: '/staff/top-performers/tips',
    TOP_PERFORMERS_HOURS: '/staff/top-performers/hours',
    LOW_PERFORMERS: '/staff/low-performers',
    STATISTICS: '/staff/statistics',
    ORDERS: (id: number | string) => `/staff/${id}/orders`,
    RATINGS: (id: number | string) => `/staff/${id}/ratings`
  },

  // Analytics
  ANALYTICS: {
    DASHBOARD: '/analytics/dashboard',
    SALES: '/analytics/sales',
    STAFF_SALES: '/analytics/staff-performance',
    DRINK_POPULARITY: '/analytics/drinks/popularity',
    DRINK_REVENUE: '/analytics/drinks/revenue',
    DRINK_PERFORMANCE: '/analytics/drinks/performance',
    STAFF_PERFORMANCE: (period: string) => `/analytics/staff-performance?period=${period}`,
    STAFF_RATINGS: '/ratings/staff/ratings',
    INVENTORY_CONSUMPTION: '/analytics/inventory/consumption',
    INVENTORY_ALERTS: '/analytics/low-stock',
    INVENTORY: '/analytics/inventory',
    REVENUE_TRENDS: '/analytics/revenue-trends',
    CATEGORY_PERFORMANCE: '/analytics/category-performance',
    TOP_PERFORMERS_LIST: '/analytics/top-performers',
    REPORTS_DAILY: '/analytics/reports/daily',
    REPORTS_MONTHLY: '/analytics/reports/monthly',
  },

  // Ratings service
  RATINGS: {
    SERVICE_RATINGS: '/ratings/pros/ratings',
    RATE: '/ratings/client/rate',
    AVERAGE: (providerId: number | string) => `/ratings/statistique/averageRating/totalPeopleRanting/${providerId}`,
    STAFF_RATINGS: '/ratings/staff/ratings',
  },

  // Notifications service
  NOTIFICATIONS: {
    LIST: '/notifications/notif',
    UNREAD_COUNT: '/notifications/unread-count',
    MARK_READ: (id: number | string) => `/notifications/mark-read/${id}`,
    MARK_ALL_READ: '/notifications/mark-all-read',
    REGISTER_TOKEN: '/notifications/device-tokens/register',
    USER_TOKENS: (userId: number | string) => `/notifications/device-tokens/user/${userId}`,
    DELETE_TOKEN: (token: string) => `/notifications/device-tokens/${encodeURIComponent(token)}`,
    TEST_PUSH: '/notifications/device-tokens/test-push',
  },

  // Livechat service — gateway rewrites /livechat/** → /api/v1/livechat/**
  LIVECHAT: {
    CREATE_SESSION: '/livechat/chat_session/create',
    START_WITH_BAR: (barId: string) => `/livechat/chat_session/with-bar/${encodeURIComponent(barId)}`,
    MINE: '/livechat/chat_session/mine',
    INCOMING: '/livechat/chat_session/incoming',
    BY_USER: '/livechat/chat_session/getAllByUserId',
    BY_PROVIDER: '/livechat/chat_session/getAllByServiceProviderId',
    BY_BOTH: (providerId: string, userId: string) => `/livechat/chat_session/getAllByProviderIdAndUserId/${providerId}/${userId}`,
    IN_PROGRESS: (providerId: string, userId: string) => `/livechat/chat_session/getByProviderIdAndUserIdInProgress/${providerId}/${userId}`,
    MESSAGES: '/livechat/chat/getAllBySession',
    SEND_MESSAGE: '/livechat/chat/send',
    // SockJS endpoint (gateway rewrites to /api/v1/livechat/ws-chat on the service)
    WS: '/livechat/ws-chat',
    // STOMP destinations (service prefixes both app + broker with /api/v1/livechat)
    STOMP_APP_SEND: '/api/v1/livechat/app/chat.sendMessage',
    STOMP_TOPIC_USER: (email: string) => `/api/v1/livechat/topic/user.${email}`,
  },

  // Bar discovery (public)
  //
  // Bare '/bars/...', NOT '/api/v1/bars/...'. These are appended to
  // API_URLS.GATEWAY, which already ends in /api — so the old values produced
  // https://reservedine.com/api/api/v1/bars/search. nginx strips one /api,
  // leaving /api/v1/bars/search, which matches no gateway route at all, so it
  // fell through to the authenticated catch-all and answered 401. The page
  // reported that as "0 bars found — No bars found", so a broken call looked
  // like an empty platform. The gateway rewrites /bars/** to /api/v1/bars/**.
  BAR_DISCOVERY: {
    SEARCH: '/bars/search',
    NEARBY: '/bars/nearby',
    RATE: (barId: string) => `/bars/${barId}/rating`,
  },

  // Health / misc
  HEALTH: {
    ACTUATOR: '/actuator/health',
    CUSTOM: '/health'
  }
} as const;
